import { CarouselPromotionsParams, PaginationParams } from "@/types/common";
import { getSystemEnv } from "@/utils/env";

const { BACKEND_URL } = getSystemEnv();
const API_BASE = `${BACKEND_URL}/api`;

interface PromotionResponse {
  data: any[];
  pagination: {
    current: number;
    total: number;
    hasNext: boolean;
    hasPrev: boolean;
    count: number;
    totalCount: number;
  };
}

export async function getPromotions(
  params?: PaginationParams
): Promise<PromotionResponse> {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });
  }

  const response = await fetch(
    `${API_BASE}/promotions${searchParams.toString() ? `?${searchParams.toString()}` : ""
    }`
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData?.error ||
      `Урамшууллуудыг авах үед алдаа гарлаа: ${response.status}`
    );
  }

  const result = await response.json();

  return {
    data: result?.data || [],
    pagination: result?.pagination || {
      current: 1,
      total: 1,
      hasNext: false,
      hasPrev: false,
      count: 0,
      totalCount: 0,
    },
  };
}

export async function getPromotion(id: string) {
  const response = await fetch(`${API_BASE}/promotions/${id}`);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData?.error ||
      `Урамшуулаллын мэдээлэл авах үед алдаа гарлаа: ${response.status}`
    );
  }

  const result = await response.json();
  return result?.data;
}

export async function getModalPromotions() {
  const response = await fetch(`${API_BASE}/promotions/modal-promotions`);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData?.error ||
      `Модалын урамшууллуудыг авах үед алдаа гарлаа: ${response.status}`
    );
  }

  const result = await response.json();
  return result?.data || [];
}

export async function getBanners() {
  const response = await fetch(`${API_BASE}/promotions/banners`);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData?.error ||
      `Баннерүүдийг авах үед алдаа гарлаа: ${response.status}`
    );
  }

  const result = await response.json();
  return result?.data || [];
}

export async function getSettings() {
  const response = await fetch(`${API_BASE}/promotions/settings`);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData?.error ||
      `Тохиргооны мэдээлэл авах үед алдаа гарлаа: ${response.status}`
    );
  }

  const result = await response.json();
  return result?.data || [];
}

export async function getMascot() {
  const response = await fetch(`${API_BASE}/promotions/mascot`);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData?.error || `Маскот авах үед алдаа гарлаа: ${response.status}`
    );
  }

  const result = await response.json();
  return result?.data || {};
}

export async function checkRatePlan(phoneNumber: string, code: string): Promise<boolean> {
  const res = await fetch(`${API_BASE}/promotions/check-rate-plan`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ phoneNumber, code }),
  });

  if (!res.ok) {
    throw new Error("Rate plan шалгах үед алдаа гарлаа");
  }

  const json = await res.json();
  return Boolean(json?.data?.valid); 
}

export async function getPromotionTitles() {
  const response = await fetch(`${API_BASE}/promotions/titles`);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData?.error ||
      `Урамшууллуудын жагсаалт авах үед алдаа гарлаа: ${response.status}`
    );
  }

  const result = await response.json();
  return result?.data || [];
}

export async function getCategories() {
  const response = await fetch(`${API_BASE}/promotions/categories`);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData?.error ||
      `Ангилаллуудыг авах үед алдаа гарлаа: ${response.status}`
    );
  }

  const result = await response.json();
  return result?.data || [];
}

export async function getDataPromotions() {
  const response = await fetch(`${API_BASE}/promotions/data-promotions`);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData?.error ||
      `Дата урамшууллуудыг авах үед алдаа гарлаа: ${response.status}`
    );
  }

  const result = await response.json();
  return result?.data || [];
}

export async function getPhonePromotions() {
  const response = await fetch(`${API_BASE}/promotions/phone-promotions`);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData?.error ||
      `Утасны урамшууллуудыг авах үед алдаа гарлаа: ${response.status}`
    );
  }

  const result = await response.json();
  return result?.data || [];
}

export async function getNumberPromotions() {
  const response = await fetch(`${API_BASE}/promotions/number-promotions`);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData?.error ||
      `Дугаарын урамшууллуудыг авах үед алдаа гарлаа: ${response.status}`
    );
  }

  const result = await response.json();
  return result?.data || [];
}

export async function getCarouselPromotions(params?: CarouselPromotionsParams) {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          searchParams.append(key, value.join(","));
        } else {
          searchParams.append(key, String(value));
        }
      }
    });
  }

  const response = await fetch(
    `${API_BASE}/promotions/carousel-promotions${searchParams.toString() ? `?${searchParams.toString()}` : ""
    }`
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData?.error ||
      `Карусел урамшууллуудыг авах үед алдаа гарлаа: ${response.status}`
    );
  }

  const result = await response.json();
  return result?.data || [];
}

export async function getTypeOrders() {
  const response = await fetch(`${API_BASE}/promotions/type-orders`);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData?.error ||
      `Дарааллын төрлийг авах үед алдаа гарлаа: ${response.status}`
    );
  }

  const result = await response.json();
  return result?.data || {};
}

export async function PromotionSeen(id: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE}/promotions/${id}/seen`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      console.error("Seen тэмдэглэхэд алдаа:", response.status);
      return false;
    }

    const result = await response.json();
    return result?.success || false;
  } catch (error) {
    console.error("Seen тэмдэглэхэд алдаа:", error);
    return false;
  }
}

export async function PromotionTaken(id: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE}/promotions/${id}/taken`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      console.error("Taken тэмдэглэхэд алдаа:", response.status);
      return false;
    }

    const result = await response.json();
    return result?.success || false;
  } catch (error) {
    console.error("Taken тэмдэглэхэд алдаа:", error);
    return false;
  }
}
